// ─── Types ────────────────────────────────────────────────────────────────────

export type ClaimStatus =
    | "REQUESTED"
    | "ACCEPTED_PICKUP"
    | "PRE_QC"
    | "IN_SERVICE"
    | "POST_QC"
    | "DISPATCHED"
    | "DELIVERED"
    | "REJECTED"
    | "CANCELLED";

// ─── ✅ VENDOR TYPE & OPTIONS (Updated with FutSteps Nashik) ─────────────
export type Vendor = 
    | "PERFECTO_CLEANERS_DELHI" 
    | "CLEANSTEPS_LAUNDRY_MUMBAI"
    | "WILD_SNEAKER_LAUNDRY_DELHI"
    | "FUTSTEPS_NASHIK";

export const VENDOR_OPTIONS: { value: Vendor; label: string }[] = [
    { value: "PERFECTO_CLEANERS_DELHI", label: "Perfecto Cleaners - Delhi" },
    { value: "CLEANSTEPS_LAUNDRY_MUMBAI", label: "Cleansteps Laundry - Mumbai" },
    { value: "WILD_SNEAKER_LAUNDRY_DELHI", label: "Wild Sneaker Laundry - Delhi" },
    { value: "FUTSTEPS_NASHIK", label: "FutSteps - Nashik" },
];

export const formatVendor = (vendor?: Vendor | string | null): string => {
    if (!vendor) return "—";
    const found = VENDOR_OPTIONS.find((v) => v.value === vendor);
    return found ? found.label : vendor;
};

export type ClaimCredits = {
    total: number;
    used: number;
    remaining: number;
    planCode?: string;
    status?: string;
};

export type RelatedClaim = {
    id: number;
    claimStatus: ClaimStatus;
    createdAt: string;
    category?: string;
    subService?: string;
};

export type Claim = {
    id: number;
    bookingRef: string;
    pickupDate: string;
    category: string;
    subService: string;
    comments?: string | null;
    name: string;
    phone: string;
    address1: string;
    address2?: string;
    city: string;
    state: string;
    zip: string;
    dropAddress1?: string;
    dropAddress2?: string;
    dropCity?: string;
    dropState?: string;
    dropZip?: string;
    email: string;
    createdAt: string;
    files: string[];
    entitlementId?: string | null;
    claimStatus?: ClaimStatus;
    statusUpdatedAt?: string | null; 
    updatedAt?: string;
    rejectionReason?: string | null;
    preQcImages?: string[];
    postQcImages?: string[];
    rating?: number | null;
    customerReview?: string | null;
    previousClaimId?: number | null;
    previousClaim?: {
        id: number;
        rejectionReason: string | null;
        createdAt: string;
        claimStatus?: ClaimStatus;
        category?: string;
        subService?: string;
    } | null;
    fileStatuses?: Record<string, string>;
    serviceCost?: number | null;
    pickupCost?: number | null;
    dropCost?: number | null;
    totalCost?: number | null;
    credits?: ClaimCredits | null;
    relatedClaims?: RelatedClaim[];

    // ─── VENDOR & SALE DETAILS ──────────────────────────
    vendor?: Vendor | null;
    sale?: {
        id: number;
        uniqueId?: string | null; 
        productName: string;
        color?: string | null;
        size?: number | null;
        brand?: string | null;
        customerName?: string | null;
        phoneNo?: string | null;
    } | null;
};

export interface MemberFormData {
    companyName: string;
    primaryContactName: string;
    primaryContactEmail: string;
    primaryContactPhone: string;
    contractStartDate: string;
    status: string;
    securedstichRevenueShare: number | "";
    clientRevenueShare: number | "";
    shopifyStoreURL: string;
}

export type UserFormData = {
    email: string;
    password: string;
    memberUuid: string;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const fmt = (s: string): string => {
    if (!s) return "—";
    const d = new Date(s);
    return `${String(d.getDate()).padStart(2, "0")}/${String(
        d.getMonth() + 1
    ).padStart(2, "0")}/${d.getFullYear()}`;
};

export const formatStatus = (status?: ClaimStatus | string): string => {
    if (!status) return "—";
    const MAP: Record<string, string> = {
        REQUESTED: "Requested",
        ACCEPTED_PICKUP: "Accepted & Pickup",
        PRE_QC: "Pre-QC",
        IN_SERVICE: "In Service",
        POST_QC: "Post-QC",
        DISPATCHED: "Dispatched",
        DELIVERED: "Delivered",
        REJECTED: "Rejected",
        CANCELLED: "Cancelled",
    };
    return (
        MAP[status] ||
        status
            .split("_")
            .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
            .join(" ")
    );
};

export const getStatusBadgeClasses = (
    status?: ClaimStatus | string
): string => {
    const base =
        "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium";

    switch (status) {
        case "REQUESTED":
            return `${base} bg-yellow-500/10 text-yellow-400 border border-yellow-500/20`;
        case "ACCEPTED_PICKUP":
            return `${base} bg-blue-500/10 text-blue-400 border border-blue-500/20`;
        case "PRE_QC":
            return `${base} bg-cyan-500/10 text-cyan-400 border border-cyan-500/20`;
        case "IN_SERVICE":
            return `${base} bg-purple-500/10 text-purple-400 border border-purple-500/20`;
        case "POST_QC":
            return `${base} bg-indigo-500/10 text-indigo-400 border border-indigo-500/20`;
        case "DISPATCHED":
            return `${base} bg-orange-500/10 text-orange-400 border border-orange-500/20`;
        case "DELIVERED":
            return `${base} bg-green-500/10 text-green-400 border border-green-500/20`;
        case "REJECTED":
            return `${base} bg-red-500/10 text-red-400 border border-red-500/20`;
        case "CANCELLED":
            return `${base} bg-gray-500/10 text-gray-400 border border-gray-500/20`;
        default:
            return `${base} bg-gray-500/10 text-gray-500 border border-gray-500/20`;
    }
};

export const INPUT =
    "w-full px-3 py-2 bg-[#1a1a1a] border border-[#2d2d2d] rounded text-sm" +
    " text-gray-200 placeholder-gray-600 focus:outline-none focus:border-blue-600 transition-colors";

export const LABEL =
    "block text-xs text-gray-500 font-medium uppercase tracking-wide mb-1";